import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AUTOMATION_RUN_KEY,
  AUTOMATION_RUN_STATS_KEY,
  AUTOMATION_RUNS_KEY,
  AUTOMATION_RUNS_WITH_DATASETS_KEY,
  PROJECT_RUNS_KEY,
  RUN_WITH_DATASETS_KEY,
  RUNS_STATS_KEY,
  STATIC_DATASET_USAGE_KEY,
} from '@/utils/queryKeys.ts';
import { Run, Runs } from '@/types/runs.ts';

const queryKeyStartsWith = (queryKey: readonly unknown[], keyPrefix: unknown[]) =>
  keyPrefix.every((part, index) => queryKey[index] === part);

const updatePaginatedRunInCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  keyPrefix: unknown[],
  run: Run,
) => {
  queryClient
    .getQueryCache()
    .findAll({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && queryKeyStartsWith(key, keyPrefix);
      },
    })
    .forEach((query) => {
      queryClient.setQueryData<{ pages: Runs[]; pageParams: unknown[] }>(query.queryKey, (data) => {
        if (!data || !Array.isArray(data.pages)) return data;

        const updatedPages = data.pages.map((page) => {
          if (!Array.isArray(page.results)) return page;

          const runExists = page.results.some((existingRun) => existingRun.id === run.id);
          if (!runExists) return page;

          const updatedResults = page.results.map((existingRun) =>
            existingRun.id === run.id ? { ...existingRun, ...run } : existingRun,
          );

          return { ...page, results: updatedResults };
        });

        return { ...data, pages: updatedPages };
      });
    });
};

const useInvalidateRun = (automationId: string) => {
  const queryClient = useQueryClient();

  return useCallback(
    (run: Run): unknown[][] => {
      // 1. Update individual run cache
      queryClient.setQueryData<Run>(AUTOMATION_RUN_KEY(automationId, run.id), run);

      // 2. Update paginated automation runs
      updatePaginatedRunInCache(queryClient, AUTOMATION_RUNS_KEY(automationId), run);

      // 3. Update paginated project runs
      updatePaginatedRunInCache(queryClient, PROJECT_RUNS_KEY, run);

      // 4. Return keys for downstream invalidation
      return [
        RUN_WITH_DATASETS_KEY(automationId, run.id),
        AUTOMATION_RUNS_WITH_DATASETS_KEY(automationId),
        AUTOMATION_RUN_STATS_KEY(automationId),
        RUNS_STATS_KEY,
        STATIC_DATASET_USAGE_KEY(automationId),
      ];
    },
    [automationId, queryClient],
  );
};

export default useInvalidateRun;
